/**
 * Störungsdiagnose.
 *
 * Die Seite beantwortet genau eine Frage: *Warum geht gerade etwas nicht?* –
 * und liefert die Antwort so, dass sie sich abarbeiten lässt. Jeder Befund
 * nennt Ursache, Auswirkung und mindestens eine Maßnahme; wo die Maßnahme ein
 * Kommando ist, steht ein Knopf daneben.
 *
 * Die Gruppierung folgt der Ursachenkette von unten nach oben. Wer sie der
 * Reihe nach durchgeht, arbeitet automatisch in der richtigen Reihenfolge:
 * Ein Standort ohne Strom zuerst, ein nicht angekündigtes Prefix zuletzt.
 */
import { useMemo, useState } from 'react';
import type { Command, Finding, Severity } from '@hosting-sim/core';
import { Badge, Button, Card, EmptyState, Notice, PageHeader, StatGrid, Stat, type Tone } from '../components/ui';
import { useGame, useMetrics } from '../store/game';

const SEVERITY_TONE: Record<Severity, Tone> = {
  critical: 'danger',
  error: 'danger',
  warning: 'warning',
  info: 'info',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Kritisch',
  error: 'Störung',
  warning: 'Warnung',
  info: 'Hinweis',
};

/** Reihenfolge und Beschriftung der Ursachenkette. */
const LAYERS: { key: Finding['layer']; label: string; hint: string }[] = [
  { key: 'strom', label: 'Strom', hint: 'Netzanschluss, Generator, USV' },
  { key: 'geraet', label: 'Geräte', hint: 'Defekte, abgeschaltete und noch nicht eingerichtete Hardware' },
  { key: 'verkabelung', label: 'Verkabelung', hint: 'Uplinks, Switch-Ports' },
  { key: 'strecke', label: 'Strecken', hint: 'Transit, Peering, eigene Fasern' },
  { key: 'bgp', label: 'BGP', hint: 'Sitzungen und Nachbarn' },
  { key: 'ankuendigung', label: 'Ankündigungen', hint: 'Was das Internet von dir sieht' },
  { key: 'kapazitaet', label: 'Kapazität', hint: 'Volle Tabellen, volle Leitungen, volle Platten' },
];

export function Diagnostics() {
  const metrics = useMetrics();
  const run = useGame((s) => s.run);
  const [busy, setBusy] = useState<string | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(true);

  const findings = metrics?.derived.diagnostics ?? [];

  const visible = useMemo(
    () => (onlyProblems ? findings.filter((f) => f.severity !== 'info') : findings),
    [findings, onlyProblems],
  );

  const counts = useMemo(() => {
    const acc: Record<Severity, number> = { critical: 0, error: 0, warning: 0, info: 0 };
    for (const finding of findings) acc[finding.severity] += 1;
    return acc;
  }, [findings]);

  const grouped = useMemo(
    () => LAYERS.map((layer) => ({ ...layer, items: visible.filter((f) => f.layer === layer.key) })).filter((g) => g.items.length > 0),
    [visible],
  );

  async function apply(finding: Finding, index: number) {
    const remedy = finding.remedies[index];
    if (!remedy?.command) return;
    setBusy(`${finding.id}:${index}`);
    try {
      await run(remedy.command as unknown as Command);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Störungsdiagnose"
        description="Ursache, Auswirkung und Maßnahme – in der Reihenfolge, in der man sie abarbeitet."
        actions={
          <Button variant={onlyProblems ? 'primary' : 'ghost'} onClick={() => setOnlyProblems((v) => !v)}>
            {onlyProblems ? 'Nur Störungen' : 'Alles anzeigen'}
          </Button>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Kritisch" value={counts.critical} tone={counts.critical > 0 ? 'negative' : 'neutral'} hint="Kunden sind betroffen" />
        <Stat label="Störungen" value={counts.error} tone={counts.error > 0 ? 'negative' : 'neutral'} />
        <Stat label="Warnungen" value={counts.warning} tone={counts.warning > 0 ? 'warning' : 'neutral'} />
        <Stat
          label="Erreichbarkeit"
          value={`${((metrics?.derived.internetReachability ?? 0) * 100).toFixed(1)} %`}
          tone={(metrics?.derived.internetReachability ?? 0) > 0.98 ? 'positive' : 'negative'}
          hint="Anteil der erreichbaren Ziele"
        />
      </StatGrid>

      {grouped.length === 0 && (
        <EmptyState
          title={onlyProblems ? 'Keine Störungen' : 'Nichts zu berichten'}
          description={
            onlyProblems
              ? 'Alle Geräte laufen, alle Strecken tragen, alle Sitzungen stehen. Mit „Alles anzeigen“ siehst du auch Hinweise.'
              : 'Die Diagnose findet derzeit nichts, was Aufmerksamkeit braucht.'
          }
        />
      )}

      {grouped.map((group) => (
        <Card key={group.key} title={group.label} subtitle={group.hint}>
          <div className="space-y-3">
            {group.items.map((finding) => (
              <FindingRow
                key={finding.id}
                finding={finding}
                busyKey={busy}
                onApply={(index) => apply(finding, index)}
              />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function FindingRow({
  finding,
  busyKey,
  onApply,
}: {
  finding: Finding;
  busyKey: string | null;
  onApply: (index: number) => void;
}) {
  return (
    <article className="rounded-lg border divider p-3">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold">{finding.title}</h3>
        <Badge tone={SEVERITY_TONE[finding.severity]}>{SEVERITY_LABEL[finding.severity]}</Badge>
      </header>

      <dl className="mt-2 space-y-1 text-xs">
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-panel-500 dark:text-panel-400">Ursache</dt>
          <dd>{finding.cause}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-panel-500 dark:text-panel-400">Auswirkung</dt>
          <dd>{finding.impact}</dd>
        </div>
      </dl>

      {finding.remedies.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {finding.remedies.map((remedy, index) =>
            remedy.command ? (
              <Button
                key={index}
                variant={index === 0 ? 'primary' : 'secondary'}
                disabled={busyKey === `${finding.id}:${index}`}
                onClick={() => onApply(index)}
                title={remedy.note}
              >
                {remedy.label}
              </Button>
            ) : (
              <span
                key={index}
                className="rounded border divider px-2 py-1 text-xs text-panel-500 dark:text-panel-400"
                title={remedy.note}
              >
                {remedy.label}
              </span>
            ),
          )}
        </div>
      )}

      {finding.remedies.some((r) => r.note) && (
        <p className="mt-2 text-xs text-panel-500 dark:text-panel-400">
          {finding.remedies.find((r) => r.note)?.note}
        </p>
      )}
    </article>
  );
}

/**
 * Kompakte Fassung für das Dashboard: nur das Dringendste, mit Verweis auf die
 * vollständige Liste. Zeigt nichts an, wenn nichts kaputt ist.
 */
export function DiagnosticsSummary({ onOpen }: { onOpen: () => void }) {
  const metrics = useMetrics();
  const run = useGame((s) => s.run);
  const [busy, setBusy] = useState(false);

  const problems = (metrics?.derived.diagnostics ?? []).filter((f) => f.severity === 'critical' || f.severity === 'error');
  if (problems.length === 0) return null;

  const top = problems[0];
  const quickFix = top.remedies.find((r) => r.command);

  return (
    <Notice tone="danger" title={`${problems.length} offene Störung${problems.length === 1 ? '' : 'en'}`}>
      <p className="text-sm font-medium">{top.title}</p>
      <p className="mt-1 text-xs">{top.cause}</p>
      <p className="mt-0.5 text-xs text-panel-500 dark:text-panel-400">{top.impact}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {quickFix && (
          <Button
            variant="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await run(quickFix.command as unknown as Command);
              } finally {
                setBusy(false);
              }
            }}
          >
            {quickFix.label}
          </Button>
        )}
        <Button variant="ghost" onClick={onOpen}>
          Alle Störungen ansehen
        </Button>
      </div>
    </Notice>
  );
}
